import { z } from 'zod';
import type { DnsRecordType } from './dns.js';

/**
 * V2-G2: dry-run-first DNS migration from one immutable Plesk snapshot.
 * Source objects are always addressed by their stable Plesk identities;
 * names are display data and may change between snapshots.
 */

export const DNS_IMPORT_ZONE_ACTIONS = ['create', 'link', 'conflict', 'skip'] as const;
export type DnsImportZoneAction = (typeof DNS_IMPORT_ZONE_ACTIONS)[number];

export const DNS_IMPORT_RECORD_ACTIONS = [
  'create',
  'link',
  'update',
  'held',
  'skip',
  'conflict',
  'provider-owned',
] as const;
export type DnsImportRecordAction = (typeof DNS_IMPORT_RECORD_ACTIONS)[number];

export const DNS_IMPORT_RESOLUTION_CHOICES = [
  'link-panel',
  'claim-powerdns',
  'import-as-primary',
  'skip',
] as const;
export type DnsImportResolutionChoice = (typeof DNS_IMPORT_RESOLUTION_CHOICES)[number];

export interface DnsNormalizedRecord {
  sourceRecordId: string;
  sourceRecordName: string;
  name: string;
  type: DnsRecordType | 'SOA';
  ttl: number;
  content: string;
  disabled: boolean;
}

export interface DnsImportRecordPlan {
  sourceRecordId: string;
  sourceRecordName: string;
  action: DnsImportRecordAction;
  panelRecordId: string | null;
  before: DnsNormalizedRecord | null;
  after: DnsNormalizedRecord | null;
  reason: string | null;
  /** True when this difference prevents execute/cutover. */
  blocker: boolean;
}

export interface DnsImportZonePlan {
  sourceZoneId: string;
  sourceSiteId: number;
  name: string;
  subscriptionExternalId: string;
  subscriptionId: string | null;
  action: DnsImportZoneAction;
  resolution: DnsImportResolutionChoice | null;
  importAsPrimary: boolean;
  panelZoneId: string | null;
  powerdnsZoneId: string | null;
  source: {
    enabled: boolean;
    kind: 'master' | 'slave';
    masters: string[];
    soa: {
      ttl: number | null;
      refresh: number | null;
      retry: number | null;
      expire: number | null;
      minimum: number | null;
    };
    dnssec: {
      collectionStatus: 'collected' | 'not-collected' | 'unavailable' | 'failed';
      enabled: boolean | null;
      dsRecords: string[];
    };
  };
  recordDiffs: DnsImportRecordPlan[];
  providerOwned: DnsNormalizedRecord[];
  blockers: string[];
  notes: string[];
}

export interface DnsImportPlanSummary {
  create: number;
  link: number;
  conflict: number;
  skip: number;
  recordsCreate: number;
  recordsLink: number;
  recordsUpdate: number;
  recordsHeld: number;
  recordsSkip: number;
  providerOwned: number;
  blockers: number;
}

export interface DnsImportPlanDto {
  snapshotId: string;
  computedAt: string;
  zones: DnsImportZonePlan[];
  summary: DnsImportPlanSummary;
  executable: boolean;
}

export const dnsImportResolutionSchema = z.object({
  sourceZoneId: z.string().trim().min(1).max(200),
  choice: z.enum(DNS_IMPORT_RESOLUTION_CHOICES).optional(),
  importAsPrimary: z.boolean().default(false),
  panelZoneId: z.string().uuid().optional(),
}).refine((value) => value.choice !== undefined || value.importAsPrimary, {
  message: 'Choose a collision action, import as primary, or both',
});
export type DnsImportResolution = z.infer<typeof dnsImportResolutionSchema>;

export const dnsImportRequestSchema = z.object({
  snapshotId: z.string().uuid(),
  resolutions: z.array(dnsImportResolutionSchema).max(1000).default([]),
  acceptPartialWarnings: z.boolean().default(false),
});
export type DnsImportRequest = z.infer<typeof dnsImportRequestSchema>;

export interface DnsImportResultEntry {
  sourceZoneId: string;
  name: string;
  outcome: 'created' | 'linked' | 'updated' | 'skipped';
  panelZoneId: string | null;
  recordsCreated: number;
  recordsLinked: number;
  recordsUpdated: number;
  recordsPreserved: number;
  dnssec: 'regenerated' | 'disabled' | 'unchanged';
}

export interface DnsImportRunDto {
  id: string;
  snapshotId: string | null;
  status: 'completed' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  summary: DnsImportPlanSummary | null;
  error: string | null;
}

export interface DnsImportRunDetailDto extends DnsImportRunDto {
  plan: DnsImportPlanDto | null;
  resolutions: DnsImportResolution[];
  results: DnsImportResultEntry[];
}

export interface DnsImportExecuteResponse {
  run: DnsImportRunDto;
  plan: DnsImportPlanDto;
  results: DnsImportResultEntry[];
}

export type DnsEqualitySide = 'plesk' | 'panel' | 'powerdns' | `provider:${string}`;

export interface DnsEqualityDiff {
  key: string;
  kind: 'missing' | 'unexpected' | 'changed' | 'unsupported' | 'delegation' | 'dnssec';
  side: DnsEqualitySide;
  expected: string | null;
  actual: string | null;
  explanation: string | null;
  blocker: boolean;
}

export interface DnsEqualityReportDto {
  zoneId: string;
  zoneName: string;
  sourceZoneId: string;
  computedAt: string;
  sides: DnsEqualitySide[];
  diffs: DnsEqualityDiff[];
  counts: {
    sourceRecords: number;
    panelRecords: number;
    powerdnsRecords: number;
    providerRecords: Record<string, number>;
  };
  readyForCutover: boolean;
}

export interface DnsCutoverSnapshot {
  capturedAt: string;
  panelRecords: Array<{
    id: string;
    name: string;
    type: DnsRecordType;
    ttl: number;
    content: string;
    origin: 'user' | 'template' | 'auto';
    templateKey: string | null;
    autoKey: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  powerdnsRrsets: Array<{
    name: string;
    type: string;
    ttl: number;
    records: Array<{ content: string; disabled?: boolean }>;
  }>;
  powerdnsDnssec: boolean;
  powerdnsMetadata: Array<{ kind: string; metadata: string[] }>;
  providerRrsets: Record<string, Array<{
    name: string;
    type: string;
    ttl: number;
    records: string[];
  }>>;
  delegation: string[];
  dsRecords: string[];
}

export type DnsCutoverStage =
  | 'imported'
  | 'ttl-lowered'
  | 'ready'
  | 'cutover'
  | 'rolled-back';

export interface DnsCutoverStateDto {
  zoneId: string;
  sourceZoneId: string;
  snapshotId: string | null;
  stage: DnsCutoverStage;
  loweredTtl: number | null;
  ttlLoweredAt: string | null;
  readyAt: string | null;
  cutoverAt: string | null;
  rolledBackAt: string | null;
  equality: DnsEqualityReportDto | null;
  previous: DnsCutoverSnapshot | null;
}

export const dnsCutoverPrepareSchema = z.object({
  ttl: z.number().int().min(60).max(86_400),
  confirm: z.literal(true),
});

export const dnsCutoverConfirmSchema = z.object({ confirm: z.literal(true) });
